import { useState } from "react";
import { Church, Loader2, MailCheck, MailWarning, Clock, RefreshCw, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputCls, btnPrimary } from "@/components/portal/ui";

export default function PendingApproval() {
  const { user, setUser, checkAuth, logout } = useAuth();
  const [form, setForm] = useState({ gender: user.gender || "", phone: user.phone || "", birth_date: user.birth_date || "" });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const rejected = user.approval_status === "rejected";
  const verified = user.email_verified;
  const hasGender = !!user.gender;

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!form.gender) return toast.error("Pilih jenis kelamin dulu");
    setSaving(true);
    try {
      const { data } = await api.put("/portal/profile", {
        gender: form.gender, phone: form.phone || null, birth_date: form.birth_date || null,
      });
      setUser(data);
      toast.success("Data tersimpan. Menunggu persetujuan admin.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Email verifikasi dikirim ulang. Cek kotak masuk / spam Anda.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setResending(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await checkAuth();
    setRefreshing(false);
    toast.message("Status diperbarui");
  };

  if (rejected) {
    return (
      <div className="p-6 pt-14" data-testid="rejected-screen">
        <span className="w-12 h-12 rounded-2xl bg-red-600 text-white grid place-items-center"><ShieldX className="w-5 h-5" /></span>
        <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-900">Pendaftaran Ditolak</h1>
        <p className="mt-2 text-slate-500">Maaf, pendaftaran Anda tidak dapat disetujui saat ini. Silakan hubungi admin/OPC gereja untuk informasi lebih lanjut.</p>
        <button type="button" onClick={logout} data-testid="rejected-logout-button" className="mt-8 w-full text-sm font-semibold text-slate-500 py-2">Keluar</button>
      </div>
    );
  }

  return (
    <div className="p-6 pt-14" data-testid="pending-approval-screen">
      <span className="w-12 h-12 rounded-2xl bg-blue-600 text-white grid place-items-center"><Church className="w-5 h-5" /></span>
      <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-900">Halo, {user.name.split(" ")[0]}!</h1>
      <p className="mt-2 text-slate-500">Akun Anda sedang diproses. Selesaikan langkah berikut agar NIG dan semua fitur portal terbuka.</p>

      {/* Step 1: email verification */}
      <div data-testid="verify-status-card" className={`mt-6 rounded-2xl border p-4 flex items-start gap-3 ${verified ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        {verified ? <MailCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <MailWarning className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
        <div className="flex-1">
          <p className="font-bold text-slate-900 text-sm">{verified ? "Email terverifikasi" : "Verifikasi email Anda"}</p>
          <p className="text-xs text-slate-600 mt-0.5">
            {verified ? "Terima kasih, email Anda sudah terkonfirmasi." : `Kami telah mengirim tautan verifikasi ke ${user.email}. Buka email dan klik tautannya.`}
          </p>
          {!verified && (
            <button type="button" onClick={resend} disabled={resending} data-testid="resend-verification-button" className="mt-2 text-xs font-bold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5 disabled:opacity-60">
              {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Kirim ulang email
            </button>
          )}
        </div>
      </div>

      {/* Step 2: complete profile (gender needed for NIG) */}
      {!hasGender && (
        <form onSubmit={saveProfile} className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="complete-profile-form">
          <p className="font-bold text-slate-900 text-sm">Lengkapi data diri</p>
          <div className="grid grid-cols-2 gap-3">
            {[["L", "Pria"], ["P", "Wanita"]].map(([v, l]) => (
              <button type="button" key={v} data-testid={`pending-gender-${v}`} onClick={() => setForm({ ...form, gender: v })} className={`rounded-xl py-3 font-bold border transition active:scale-95 ${form.gender === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
            ))}
          </div>
          <input data-testid="pending-phone-input" className={inputCls} placeholder="No. WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input data-testid="pending-birth-input" type="date" className={inputCls} value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
          <button data-testid="pending-save-button" disabled={saving} className={`${btnPrimary} w-full`}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Data"}</button>
        </form>
      )}

      {/* Step 3: waiting for admin */}
      <div data-testid="awaiting-approval-card" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3">
        <Clock className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-slate-900 text-sm">Menunggu persetujuan admin</p>
          <p className="text-xs text-slate-600 mt-0.5">Setelah admin menyetujui, NIG Anda diterbitkan dan semua fitur portal akan terbuka otomatis.</p>
        </div>
      </div>

      <button type="button" onClick={refresh} disabled={refreshing} data-testid="refresh-status-button" className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-slate-200 rounded-xl py-3 font-bold text-slate-700 hover:bg-slate-50 active:scale-95 transition disabled:opacity-60">
        {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Perbarui Status
      </button>
      <button type="button" onClick={logout} data-testid="pending-logout-button" className="mt-2 w-full text-sm font-semibold text-slate-500 py-2">Keluar</button>
    </div>
  );
}
